import React, { useMemo, useState } from "react";
import { Container } from "react-bootstrap";
import { useParams } from "react-router-dom";
import moment from "moment";

import { Breadcrumbs, CstmPagination } from "components/Common/Common";
import TableLayoutV2New from "components/TableLayoutV2New";
import ErrorComponent from "components/shared/ErrorComponent";
import LoadingIndicator from "components/shared/LoadingIndicator";
import LoadingOverlay from "components/shared/LoadingOverlay";
import PolicyPreviewModal from "components/Modals/PolicyPreviewModal";
import { POLICY_AND_PROCEDURE_V2 } from "config/paths";
import { useGetPolicyDetailsQuery } from "store/services/policies";
import { useLazyGetFileUrlQuery } from "store/services/utility";
import styles from "./PolicyDetail.module.scss";

const DetailField = ({ label, children, className = "" }) => (
  <div className={`${styles.detailField} ${className}`}>
    <h4>{label}</h4>
    <div className={styles.detailValue}>{children || "--"}</div>
  </div>
);

const therapistName = (therapist = {}) => [therapist.firstname, therapist.lastname].filter(Boolean).join(" ") || therapist.name || therapist.email || "--";

const PolicyAndProcedureDetailV2 = () => {
  const { id } = useParams();
  const [previewUrl, setPreviewUrl] = useState("");
  const [therapistSearch, setTherapistSearch] = useState("");
  const [therapistPage, setTherapistPage] = useState(1);
  const { data, isLoading, isError } = useGetPolicyDetailsQuery(id, { skip: !id });
  const [getFileUrl, { isFetching: isPreviewLoading }] = useLazyGetFileUrlQuery();

  const policy = data?.data || data?.policy || data || {};
  const attachmentKey = policy?.attachment_key || policy?.attachment?.attachment_key;
  const therapistAssignments = useMemo(() => {
    if (policy?.assignments?.length) {
      return policy.assignments
        .map((assignment) => ({
          ...assignment,
          therapist: assignment?.assignedUser || assignment?.user || assignment?.therapist,
        }))
        .filter((assignment) => assignment.therapist);
    }

    return (policy?.users || policy?.assignedUsers || []).map((therapist) => ({
      id: therapist.id,
      therapist,
      agreed_at: therapist.agreed_at,
    }));
  }, [policy]);
  const filteredTherapistAssignments = useMemo(() => {
    const searchValue = therapistSearch.trim().toLowerCase();
    if (!searchValue) return therapistAssignments;

    return therapistAssignments.filter(({ therapist }) =>
      `${therapist?.firstname || ""} ${therapist?.lastname || ""} ${therapist?.email || ""}`
        .toLowerCase()
        .includes(searchValue)
    );
  }, [therapistAssignments, therapistSearch]);
  const therapistPageSize = 10;
  const therapistTotalPages = Math.max(1, Math.ceil(filteredTherapistAssignments.length / therapistPageSize));
  const activeTherapistPage = Math.min(therapistPage, therapistTotalPages);
  const paginatedTherapists = filteredTherapistAssignments.slice((activeTherapistPage - 1) * therapistPageSize, activeTherapistPage * therapistPageSize);

  const therapistColumns = [
    {
      head: "THERAPIST NAME",
      accessor: "therapist_name",
      component: (item) => therapistName(item.therapist),
    },
    {
      head: "EMAIL",
      accessor: "email",
      component: (item) => item?.therapist?.email || "--",
    },
    {
      head: "ACCEPTED POLICY AT",
      accessor: "agreed_at",
      component: (item) => item?.agreed_at ? moment(item.agreed_at).format("DD/MM/YYYY") : "Pending",
    },
  ];

  const handlePreview = async () => {
    if (!attachmentKey) return;

    try {
      const result = await getFileUrl(attachmentKey);
      if (!result?.error && result?.data) setPreviewUrl(result.data);
    } catch (error) {
      console.error("Unable to retrieve policy document: ", error);
    }
  };

  const breadcrumbItems = [
    { label: "Policies & Procedures", path: POLICY_AND_PROCEDURE_V2 },
    { label: policy?.name || "Policy Details" },
  ];

  if (isLoading) return <div className="vh-100"><LoadingIndicator /></div>;
  if (isError) return <ErrorComponent />;

  return (
    <section className={`${styles.policyDetail} position-relative`}>
      {isPreviewLoading && <LoadingOverlay />}
      <PolicyPreviewModal documentUrl={previewUrl} onHide={() => setPreviewUrl("")} />

      <div className="mb-4"><Breadcrumbs themeBlueV2 items={breadcrumbItems} /></div>
      <h2 className={styles.sectionTitle}>Policy Details</h2>
      <div className={styles.sectionDivider} />

      <div className={styles.detailsGrid}>
        <DetailField label="Policy Name">{policy?.name || policy?.title}</DetailField>
        <DetailField label="Audience Type">
          {policy?.audience_type === "selected" ? "Selected Therapists" : policy?.audience_type === "global" ? "Global" : policy?.audience_type}
        </DetailField>
        <DetailField label="Description" className={styles.descriptionField}>{policy?.description}</DetailField>
        <DetailField label="Policy Document">
          {attachmentKey && <button type="button" className={styles.viewPolicyButton} onClick={handlePreview}>View Policy</button>}
        </DetailField>
      </div>

      <section className={styles.therapistsSection}>
        <h2 className={styles.sectionTitle}>Therapists</h2>
        <div className={styles.sectionDivider} />
        <div className={styles.therapistToolbar}>
          <label htmlFor="policy-therapist-search">Search</label>
          <input
            id="policy-therapist-search"
            type="search"
            style={{borderRadius: 0, borderColor: "black", outline: "none"}}
            value={therapistSearch}
            placeholder="Name or email"
            onChange={(event) => {
              setTherapistSearch(event.target.value);
              setTherapistPage(1);
            }}
          />
        </div>
        <div className={styles.therapistsTable}>
          <TableLayoutV2New
            data={paginatedTherapists}
            column={therapistColumns}
            height="auto"
            noDataMsg="No therapists assigned"
          />
        </div>
        <CstmPagination
          totalPages={therapistTotalPages}
          activePage={activeTherapistPage}
          className="paginationV2"
          handlePageChange={setTherapistPage}
        />
      </section>
    </section>
  );
};

const PolicyAndProcedureDetailPage = () => (
  <Container fluid>
    <PolicyAndProcedureDetailV2 />
  </Container>
);

export default PolicyAndProcedureDetailPage;
